import { TFunction } from 'i18next';
import { IntegrationConfigBase } from 'models/integrations/integrationConfigBase';
import { IIntegrationEvidence } from 'models/integrations/integrationEvidence';
import { toLocaleDateTime } from 'utils/datetime';

//The API's GitHub_VersionControlConfig limits
export const gitHubMaxMinimumActiveRepositories = 1000;
export const gitHubMaxDaysSinceLastPush = 3650;

//
// Config class: the API's GitHub_VersionControlConfig; no repository selection, it counts every granted repository
//
export class IntegrationConfig_GitHub_VersionControl extends IntegrationConfigBase {
  minimumActiveRepositories: number = 1;

  //0: any push ever counts
  maxDaysSinceLastPush: number = 90;

  includeArchived: boolean = false;

  includeForks: boolean = false;

  static from(stored: unknown): IntegrationConfig_GitHub_VersionControl {
    const raw = (stored ?? {}) as Partial<IntegrationConfig_GitHub_VersionControl>;
    const config = new IntegrationConfig_GitHub_VersionControl();
    if (typeof raw.minimumActiveRepositories === 'number')
      config.minimumActiveRepositories = raw.minimumActiveRepositories;
    if (typeof raw.maxDaysSinceLastPush === 'number') config.maxDaysSinceLastPush = raw.maxDaysSinceLastPush;
    config.includeArchived = raw.includeArchived === true;
    config.includeForks = raw.includeForks === true;

    return config;
  }

  clone(): IntegrationConfig_GitHub_VersionControl {
    return IntegrationConfig_GitHub_VersionControl.from(this);
  }

  isEqual(other: IntegrationConfig_GitHub_VersionControl): boolean {
    return (
      this.minimumActiveRepositories === other.minimumActiveRepositories &&
      this.maxDaysSinceLastPush === other.maxDaysSinceLastPush &&
      this.includeArchived === other.includeArchived &&
      this.includeForks === other.includeForks
    );
  }

  //The problems of the API's Validate; empty when valid
  getProblems(t: TFunction<string[]>): string[] {
    const problems: string[] = [];

    if (
      !Number.isInteger(this.minimumActiveRepositories) ||
      this.minimumActiveRepositories < 1 ||
      this.minimumActiveRepositories > gitHubMaxMinimumActiveRepositories
    ) {
      problems.push(
        t('integrations:GitHub_VersionControl.Problems.MinimumActive', { max: gitHubMaxMinimumActiveRepositories }),
      );
    }

    if (
      !Number.isInteger(this.maxDaysSinceLastPush) ||
      this.maxDaysSinceLastPush < 0 ||
      this.maxDaysSinceLastPush > gitHubMaxDaysSinceLastPush
    ) {
      problems.push(t('integrations:GitHub_VersionControl.Problems.MaxDays', { max: gitHubMaxDaysSinceLastPush }));
    }

    return problems;
  }
}

//The visibility GitHub reports: public, private or internal; anything else as is
export const getGitHubVisibilityText = (visibility: string | undefined, t: TFunction<string[]>): string => {
  switch (visibility?.toLowerCase()) {
    case 'public':
      return t('integrations:GitHub_VersionControl.Visibility.Public');
    case 'private':
      return t('integrations:GitHub_VersionControl.Visibility.Private');
    case 'internal':
      return t('integrations:GitHub_VersionControl.Visibility.Internal');
    case undefined:
    case '':
      return t('integrations:GitHubBaseline.Unknown');
    default:
      return visibility ?? '';
  }
};

//
// Evidence classes
//
export class VersionControlRepository {
  name: string = '';

  visibility: string | undefined = undefined;

  defaultBranch: string | undefined = undefined;

  //undefined: never pushed
  pushedAtUtc: Date | undefined = undefined;

  archived: boolean = false;

  fork: boolean = false;

  active: boolean = false;
}

export class GitHub_VersionControlEvidence implements IIntegrationEvidence {
  organization: string = '';

  evaluatedAtUtc: Date = new Date();

  //the repositories counted: granted, minus archived and forks unless included
  totalRepositories: number = 0;

  activeRepositories: number = 0;

  pass: boolean = false;

  //the table holds the 50 most recently pushed repositories only, or the listing was cut off
  isCapped: boolean = false;

  //the settings this outcome was judged by; undefined in a result without them
  config: IntegrationConfig_GitHub_VersionControl | undefined = undefined;

  //latest push first
  repositories: VersionControlRepository[] = [];

  //"n repositories, m active in the last d days", or with any push when the period is 0
  getCountText(t: TFunction<string[]>): string {
    const days = this.config?.maxDaysSinceLastPush ?? 0;

    return days === 0
      ? t('integrations:GitHub_VersionControl.Result.CountAnyPush', {
          count: this.totalRepositories,
          active: this.activeRepositories,
        })
      : t('integrations:GitHub_VersionControl.Result.Count', {
          count: this.totalRepositories,
          active: this.activeRepositories,
          days,
        });
  }

  toMarkdownReport(t: TFunction<['translation', 'integrations']>): string {
    const typedT = t as TFunction<string[]>;
    const yesNo = (value: boolean) => (value ? t('translation:General.Words.Yes') : t('translation:General.Words.No'));
    const sb: string[] = [];

    sb.push(
      `## ${t('integrations:GitHub_VersionControl.Report.SummaryHeading', {
        organization: this.organization,
        date: toLocaleDateTime(this.evaluatedAtUtc),
      })}`,
    );
    sb.push(`- ${this.getCountText(typedT)}`);
    sb.push(
      `- ${t('integrations:GitHub_VersionControl.Report.MinimumLine', {
        minimum: this.config?.minimumActiveRepositories ?? 1,
      })}`,
    );
    if (this.isCapped) {
      sb.push(`- ${t('integrations:GitHub_VersionControl.Result.Capped')}`);
    }
    sb.push(
      `- ${t('integrations:GitHubBaseline.Report.OutcomeLine', {
        outcome: this.pass
          ? t('integrations:GitHubBaseline.Result.Pass')
          : t('integrations:GitHubBaseline.Result.Fail'),
      })}`,
    );
    sb.push('');
    sb.push(
      `| ${t('integrations:GitHubBaseline.Columns.Repository')} | ${t(
        'integrations:GitHub_VersionControl.Columns.Visibility',
      )} | ${t('integrations:GitHub_VersionControl.Columns.DefaultBranch')} | ${t(
        'integrations:GitHub_VersionControl.Columns.LastPush',
      )} | ${t('integrations:GitHub_VersionControl.Columns.Active')} |`,
    );
    sb.push('| --- | --- | --- | --- | --- |');
    for (const repository of this.repositories) {
      sb.push(
        `| ${repository.name} | ${getGitHubVisibilityText(repository.visibility, typedT)} | ${
          repository.defaultBranch ?? ''
        } | ${
          repository.pushedAtUtc
            ? toLocaleDateTime(repository.pushedAtUtc)
            : t('integrations:GitHub_VersionControl.Result.NeverPushed')
        } | ${yesNo(repository.active)} |`,
      );
    }

    return sb.join('\n');
  }
}

export const parse_Evidence_GitHub_VersionControl = (
  raw: string | undefined,
): GitHub_VersionControlEvidence | undefined => {
  if (!raw) return undefined;

  const rawEvidence = JSON.parse(raw);
  if (!rawEvidence) return undefined;

  const evidence = new GitHub_VersionControlEvidence();
  evidence.organization = rawEvidence.organization ?? '';
  evidence.evaluatedAtUtc = rawEvidence.evaluatedAtUtc ? new Date(rawEvidence.evaluatedAtUtc) : new Date();
  evidence.totalRepositories = rawEvidence.totalRepositories ?? 0;
  evidence.activeRepositories = rawEvidence.activeRepositories ?? 0;
  evidence.pass = rawEvidence.pass ?? false;
  evidence.isCapped = rawEvidence.isCapped ?? false;
  evidence.config = rawEvidence.config ? IntegrationConfig_GitHub_VersionControl.from(rawEvidence.config) : undefined;
  evidence.repositories = Array.isArray(rawEvidence.repositories)
    ? rawEvidence.repositories.map((item: Record<string, unknown>) => {
        const repository = new VersionControlRepository();
        repository.name = typeof item.name === 'string' ? item.name : '';
        repository.visibility = typeof item.visibility === 'string' ? item.visibility : undefined;
        repository.defaultBranch = typeof item.defaultBranch === 'string' ? item.defaultBranch : undefined;
        repository.pushedAtUtc = typeof item.pushedAtUtc === 'string' ? new Date(item.pushedAtUtc) : undefined;
        repository.archived = item.archived === true;
        repository.fork = item.fork === true;
        repository.active = item.active === true;

        return repository;
      })
    : [];

  return evidence;
};
